"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  Clock,
  CalendarDays,
  Wallet,
  Users,
  Target,
} from "lucide-react";

const BENEFITS = [
  {
    id: "payroll",
    icon: Wallet,
    title: "Payroll, Time & Attendance",
    desc: "Track hours, manage multi-rate pay, simplify time-off requests, and run payroll from a single platform. No more double entry or manual approval chains.",
    image:
      "https://images.unsplash.com/photo-1554224155-6726b3ff858f?q=80&w=1200&auto=format&fit=crop",
  },
  {
    id: "hiring",
    icon: Users,
    title: "Hiring & Onboarding",
    desc: "Post jobs, track candidates through a custom pipeline, and get new hires productive on day one with automated onboarding checklists.",
    image:
      "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?q=80&w=1200&auto=format&fit=crop",
  },
  {
    id: "data",
    icon: Target,
    title: "HR Data & Reporting",
    desc: "Pre-built and custom reports give you headcount, attrition, and cost visibility without exporting a single spreadsheet.",
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=1200&auto=format&fit=crop",
  },
  {
    id: "experience",
    icon: CalendarDays,
    title: "Employee Experience & Performance",
    desc: "Goals, continuous feedback, and review cycles that keep every employee's growth on a visible, trackable path.",
    image:
      "https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=1200&auto=format&fit=crop",
  },
];

export default function Benefits() {
  const [openId, setOpenId] = useState(BENEFITS[0].id);
  const activeBenefit = BENEFITS.find((b) => b.id === openId);

  return (
    <section className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6">
        <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-foreground text-center mb-16">
          Combine all your HR systems into one platform
        </h2>

        <div className="grid md:grid-cols-2 gap-14 items-start">
          <div className="border-t border-border">
            {BENEFITS.map((b) => {
              const Icon = b.icon;
              const isOpen = openId === b.id;
              return (
                <div key={b.id} className="border-b border-border">
                  <button
                    onClick={() => setOpenId(isOpen ? null : b.id)}
                    className="w-full flex items-center justify-between py-6 text-left group"
                  >
                    <span className="flex items-center gap-4">
                      <span
                        className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${isOpen ? "bg-primary/15" : "bg-muted"}`}
                      >
                        <Icon
                          size={17}
                          className={
                            isOpen ? "text-primary" : "text-muted-foreground"
                          }
                        />
                      </span>
                      <span
                        className={`text-lg font-semibold transition-colors ${isOpen ? "text-foreground" : "text-muted-foreground group-hover:text-foreground"}`}
                      >
                        {b.title}
                      </span>
                    </span>
                    <ChevronDown
                      size={18}
                      className={`text-muted-foreground transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <p className="pb-7 pl-13 text-sm text-muted-foreground leading-relaxed max-w-md">
                          {b.desc}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>

          <div className="sticky top-24">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeBenefit?.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.35 }}
                className="relative rounded-2xl overflow-hidden border border-border shadow-xl aspect-[4/3]"
              >
                <img
                  src={activeBenefit?.image}
                  alt={activeBenefit?.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                {/* <div className="absolute bottom-5 left-5 right-5 bg-card/95 backdrop-blur-sm border border-border rounded-xl px-4 py-3">
                  <p className="text-sm font-semibold text-foreground">
                    {activeBenefit?.title}
                  </p>
                </div> */}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
